import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { EscritorioMembro } from '@/api/client';
import { atribuirResponsaveis, fetchResponsaveis, type ModoDeAtribuir, type Responsavel } from '@/api/distribuicao';
import type { Equipe } from '@/api/equipes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { Opcao } from './DestinosPicker';

const chave = (r: Responsavel) => `${r.tipo}:${r.valor}`;

/** O processo mudou de mãos: a fila, quem cuida e as listas da visão do escritório. */
export function invalidarDistribuicao(queryClient: QueryClient, escritorioId: string) {
  for (const k of [
    queryKeys.escritorios.fila(escritorioId),
    queryKeys.escritorios.distribuicaoPendente,
    queryKeys.escritorios.aplicadas(escritorioId),
    ['escritorios', escritorioId, 'responsaveis'],
    queryKeys.processos.all,
    queryKeys.comunicacoes.all,
    // Os prazos e eventos dos processos de que se cuida (Bloco 5).
    queryKeys.calendario.eventos,
    queryKeys.calendario.agenda,
    queryKeys.dashboard.regua,
  ]) {
    queryClient.invalidateQueries({ queryKey: k });
  }
}

/** Os responsáveis em texto: "Equipe Funasa 47% e Dra. Helena". */
export function descreverResponsaveis(rs: Responsavel[], equipes: Equipe[], membros: EscritorioMembro[]): string {
  const nomes = rs.map((r) =>
    r.tipo === 'EQUIPE'
      ? `Equipe ${equipes.find((e) => e.id === r.valor)?.nome ?? ''}`.trim()
      : (membros.find((m) => m.usuario_id === r.valor)?.usuario_nome ?? 'um membro'),
  );
  if (nomes.length <= 1) return nomes[0] ?? '';
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

interface ResponsaveisDialogProps {
  escritorioId: string;
  processoIds: string[];
  /** O que aparece embaixo do título: o número do processo, ou "3 processos". */
  rotulo: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  membros: EscritorioMembro[];
  equipes: Equipe[];
  /** Quem cuida hoje (um processo só): começa marcado. Ausente num processo só: pergunta
   * ao servidor (o processo próprio sem distribuição, cujo dono a tela não conhece). */
  iniciais?: Responsavel[];
  onSalvo?: () => void;
}

/** "Quem cuida": o Gestor escolhe pessoas e equipes para um ou vários processos (§19). */
export function ResponsaveisDialog({
  escritorioId,
  processoIds,
  rotulo,
  open,
  onOpenChange,
  membros,
  equipes,
  iniciais,
  onSalvo,
}: ResponsaveisDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Quem cuida</DialogTitle>
          <DialogDescription className="break-words">{rotulo}</DialogDescription>
        </DialogHeader>
        {open &&
          (iniciais === undefined && processoIds.length === 1 ? (
            <ComOsDeHoje
              escritorioId={escritorioId}
              processoId={processoIds[0]}
              membros={membros}
              equipes={equipes}
              onFechar={() => onOpenChange(false)}
              onSalvo={onSalvo}
            />
          ) : (
            <Corpo
              escritorioId={escritorioId}
              processoIds={processoIds}
              membros={membros}
              equipes={equipes}
              iniciais={iniciais ?? []}
              onFechar={() => onOpenChange(false)}
              onSalvo={onSalvo}
            />
          ))}
      </DialogContent>
    </Dialog>
  );
}

/** Um processo só, sem saber quem cuida: lê do servidor antes de mostrar a escolha. */
function ComOsDeHoje({
  escritorioId,
  processoId,
  ...resto
}: Omit<CorpoProps, 'processoIds' | 'iniciais'> & { processoId: string }) {
  const hoje = useQuery({
    queryKey: queryKeys.escritorios.responsaveis(escritorioId, processoId),
    queryFn: () => fetchResponsaveis(escritorioId, processoId),
    staleTime: 0,
  });
  if (hoje.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size={32} label="Carregando quem cuida" />
      </div>
    );
  }
  // Sem saber quem cuida hoje, a escolha começaria vazia e "Salvar" tiraria quem cuida.
  if (!hoje.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{classifyError(hoje.error).message}</AlertDescription>
      </Alert>
    );
  }
  return <Corpo escritorioId={escritorioId} processoIds={[processoId]} iniciais={hoje.data.responsaveis} {...resto} />;
}

type CorpoProps = Omit<ResponsaveisDialogProps, 'open' | 'onOpenChange' | 'rotulo'> & {
  iniciais: Responsavel[];
  onFechar: () => void;
};

function Corpo({ escritorioId, processoIds, membros, equipes, iniciais, onFechar, onSalvo }: CorpoProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [escolhidos, setEscolhidos] = useState<Map<string, Responsavel>>(
    () => new Map(iniciais.map((r) => [chave(r), r])),
  );
  const [modo, setModo] = useState<ModoDeAtribuir>('SUBSTITUIR');
  const varios = processoIds.length > 1;
  const alternar = (r: Responsavel) =>
    setEscolhidos((atual) => {
      const novo = new Map(atual);
      if (novo.has(chave(r))) novo.delete(chave(r));
      else novo.set(chave(r), r);
      return novo;
    });

  const salvar = useMutation({
    mutationFn: () => atribuirResponsaveis(escritorioId, processoIds, [...escolhidos.values()], modo),
    onSuccess: (r) => {
      const partes = [r.atribuidos === 1 ? '1 processo distribuído' : `${r.atribuidos} processos distribuídos`];
      if (r.ignorados > 0) {
        partes.push(
          `${r.ignorados} ${r.ignorados === 1 ? 'ficou' : 'ficaram'} de fora (não estão no escritório)`,
        );
      }
      showSuccess(partes.join('; '));
      invalidarDistribuicao(queryClient, escritorioId);
      onSalvo?.();
      onFechar();
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const ordenados = [...membros].sort((a, b) => a.usuario_nome.localeCompare(b.usuario_nome, 'pt-BR'));

  return (
    <div className="space-y-4">
      {varios && (
        <RadioGroup value={modo} onValueChange={(v) => setModo(v as ModoDeAtribuir)} className="gap-2">
          {(
            [
              ['SUBSTITUIR', 'Trocar quem cuida', 'Os escolhidos passam a ser os únicos responsáveis.'],
              ['ADICIONAR', 'Acrescentar', 'Os escolhidos se somam a quem já cuida de cada processo.'],
            ] as const
          ).map(([valor, titulo, ajuda]) => (
            <Label
              key={valor}
              htmlFor={`modo-atribuir-${valor}`}
              className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3 font-normal has-[[data-state=checked]]:border-primary"
            >
              <RadioGroupItem id={`modo-atribuir-${valor}`} value={valor} className="mt-0.5" />
              <span>
                <span className="block font-medium">{titulo}</span>
                <span className="block text-xs text-muted-foreground">{ajuda}</span>
              </span>
            </Label>
          ))}
        </RadioGroup>
      )}

      {equipes.length > 0 && (
        <div className="space-y-2" role="group" aria-labelledby="responsaveis-equipes">
          <p id="responsaveis-equipes" className="text-sm font-medium">
            Equipes
          </p>
          <div className="flex flex-wrap gap-2">
            {equipes.map((e) => {
              const r: Responsavel = { tipo: 'EQUIPE', valor: e.id };
              return (
                <Opcao key={e.id} ativa={escolhidos.has(chave(r))} onClick={() => alternar(r)}>
                  {e.nome}
                </Opcao>
              );
            })}
          </div>
        </div>
      )}
      <div className="space-y-2" role="group" aria-labelledby="responsaveis-pessoas">
        <p id="responsaveis-pessoas" className="text-sm font-medium">
          Pessoas
        </p>
        <div className="flex flex-wrap gap-2">
          {ordenados.map((m) => {
            const r: Responsavel = { tipo: 'USUARIO', valor: m.usuario_id };
            return (
              <Opcao key={m.usuario_id} ativa={escolhidos.has(chave(r))} onClick={() => alternar(r)}>
                {m.usuario_nome}
              </Opcao>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Quem cuida vê o processo, trabalha nele e recebe as intimações dele.
      </p>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" className="h-11 sm:h-9" onClick={onFechar}>
          Cancelar
        </Button>
        <Button
          className="h-11 sm:h-9"
          onClick={() => salvar.mutate()}
          disabled={escolhidos.size === 0 || salvar.isPending}
        >
          {salvar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
          Salvar
        </Button>
      </div>
    </div>
  );
}
